import { UISchema } from '@/type';
import { MaterialEditorConfig, TextureSlotSelect } from '../type';

export function getPhysicalMaterialSchema(config: MaterialEditorConfig): UISchema {
  const mapEnable = config.textureSlots.map?.config?.enable;
  return [
    {
      uiType: 'InputText',
      fieldName: 'type',
      label: '材质类型',
      disabled: true,
    },
    {
      uiType: 'ColorPicker',
      fieldName: 'color',
      label: '基础颜色',
      type: ['pure'],
      tooltip: '控制材质的基本颜色，决定物体表面的主色调。',
    },
    // 基础纹理和颜色
    {
      uiType: 'Select',
      fieldName: 'textureSlots.map.value',
      label: '基础贴图',
      options: (config.textureSlots.map as TextureSlotSelect).options,
      allowClear: true,
    },
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '贴图设定',
      hidden: !mapEnable,
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'map_saturation',
          label: '饱和度',
          min: 0,
          max: 2,
          tooltip: '调整贴图饱和度（贴图颜色的灰阶与原始颜色的插值，0为颜色的灰阶，1为原始颜色）',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'map_contrast',
          label: '对比度',
          min: 0,
          max: 2,
          tooltip: '调整对比度（中性灰与原始颜色插值，0 为中性灰，1 为原始颜色）',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'map_brightness',
          label: '亮度',
          min: 0,
          max: 2,
          tooltip: '调整亮度（该数值与颜色贴图rgb相乘）',
        },
      ],
    },
    // 金属度和粗糙度
    {
      uiType: 'InputSlider',
      fieldName: 'metalness',
      label: '金属度系数',
      min: 0,
      max: 1,
      tooltip:
        '定义材质是否为金属。金属度值为 0 时为非金属（如木材、塑料），值为 1 时为完全金属（如金属表面）。',
    },
    {
      uiType: 'Select',
      fieldName: 'textureSlots.metalnessMap.value',
      label: '金属度贴图',
      options: (config.textureSlots.metalnessMap as TextureSlotSelect).options,
      allowClear: true,
    },
    {
      uiType: 'InputSlider',
      fieldName: 'roughness',
      label: '粗糙度系数',
      min: 0,
      max: 1,
      tooltip: '控制表面的光滑程度。',
    },
    {
      uiType: 'Select',
      fieldName: 'textureSlots.roughnessMap.value',
      label: '粗糙度贴图',
      options: (config.textureSlots.roughnessMap as TextureSlotSelect).options,
      allowClear: true,
    },
    {
      uiType: 'InputSlider',
      fieldName: 'reflectivity',
      label: '反射率',
      min: 0,
      max: 1,
      tooltip:
        '决定材质表面反射光线的强度，影响物体的亮度和镜面反射效果。高反射率的材质，如金属或水面，能够显著反射环境光。',
    },
    {
      uiType: 'InputSlider',
      fieldName: 'ior',
      label: '折射率(IOR)',
      min: 1,
      max: 2.333,
      tooltip:
        '定义光线进入材质后发生弯曲的程度。折射率越高，光线弯曲的角度越大，常用于模拟透明材质如玻璃、水和宝石的折射效果。',
    },
    // 色散
    {
      uiType: 'InputSlider',
      fieldName: 'dispersion',
      label: '色散',
      min: 0,
      max: 1,
      tooltip:
        '描述光在材质中的折射率变化，模拟不同波长光的分散效应，通常用于模拟如钻石的彩色折射效果。',
    },
    // 透明
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '透明',
      children: [
        {
          uiType: 'CheckBox',
          fieldName: 'transparent',
          label: '透明',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'opacity',
          label: '不透明度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.alphaMap.value',
          label: '透明贴图',
          options: (config.textureSlots.alphaMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'Select',
          fieldName: 'blend',
          label: '混合',
          options: [
            { key: 0, label: '正常' },
            { key: 1, label: '相加' },
          ],
        },
        {
          uiType: 'InputSlider',
          fieldName: 'alphaTest',
          label: '透明阈值',
          min: 0,
          max: 1,
          tooltip:
            '通过对 Alpha 值进行比较操作剔除低透明度的像素，用于处理具有透明区域的纹理，例如树叶、栅格等复杂模型。',
        },
      ],
    },
    // 虹彩
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '虹彩',
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'iridescence',
          label: '虹彩',
          min: 0,
          max: 1,
          tooltip:
            '模拟透明或半透明材质表面由于折射或干涉现象产生的彩虹效果。该效应常见于珠宝、油膜或玻璃表面。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.iridescenceMap.value',
          label: '虹彩贴图',
          options: (config.textureSlots.iridescenceMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'iridescenceIOR',
          label: '虹彩折射率',
          min: 1,
          max: 2.333,
          tooltip: '控制虹彩效应中光线折射的强度，折射率越高，虹彩效果越显著。',
        },
        {
          uiType: 'InputSlider',
          fieldName: 'iridescenceThicknessRange[1]',
          label: '虹彩厚度',
          min: 0,
          max: 1,
          tooltip: '定义产生虹彩效应的物体厚度，影响光的折射和色散效果。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.iridescenceThicknessMap.value',
          label: '虹彩厚度贴图',
          options: (config.textureSlots.iridescenceThicknessMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputNumberGroup',
          label: '厚度范围',
          tooltip: '设定虹彩效果的厚度变化范围，控制不同厚度对虹彩效应的影响。',
          inputNumbers: [
            {
              fieldName: 'iridescenceThicknessRange[0]',
            },
            {
              fieldName: 'iridescenceThicknessRange[1]',
            },
          ],
        },
      ],
    },
    // 清漆
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '清漆',
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'clearcoat',
          label: '清漆强度',
          min: 0,
          max: 1,
          tooltip:
            '影响材质表面清漆层的光反射强度，决定表面反光的清晰度和强度。较高的清漆强度使物体表面呈现出强烈的镜面反射效果。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.clearcoatMap.value',
          label: '清漆贴图',
          options: (config.textureSlots.clearcoatMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'clearcoatRoughness',
          label: '清漆粗糙度',
          min: 0,
          max: 1,
          tooltip:
            '控制材质表面清漆的光滑度。较高的粗糙度值会使表面反射光变得模糊，模拟磨砂效果；较低的值则产生光滑的镜面反射。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.clearcoatRoughnessMap.value',
          label: '清漆粗糙度贴图',
          options: (config.textureSlots.clearcoatRoughnessMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.clearcoatNormalMap.value',
          label: '清漆法线贴图',
          options: (config.textureSlots.clearcoatNormalMap as TextureSlotSelect).options,
          allowClear: true,
        },
      ],
    },
    // 自发光
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '自发光',
      children: [
        {
          uiType: 'ColorPicker',
          fieldName: 'emissive',
          label: '自发光颜色',
          type: ['pure'],
          disableAlpha: true,
          tooltip: '模拟材质表面自发光的颜色，用于模拟如 LED 屏幕、霓虹灯等效果。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.emissiveMap.value',
          label: '自发光贴图',
          options: (config.textureSlots.emissiveMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'emissiveIntensity',
          label: '自发光强度',
          min: 0,
          max: 1,
        },
      ],
    },
    // 光泽
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '光泽',
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'sheen',
          label: '光泽度',
          min: 0,
          max: 1,
          tooltip:
            '定义材质表面反射光的强度与清晰度。较高的光泽度使表面呈现出镜面反射效果，低光泽度则表现为柔和的漫反射光。',
        },
        {
          uiType: 'ColorPicker',
          fieldName: 'sheenColor',
          label: '光泽颜色',
          type: ['pure'],
          disableAlpha: true,
          tooltip: '指定材质表面反射光的颜色，通常用于模拟表面有颜色的反射光或金属的色彩反射。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.sheenColorMap.value',
          label: '光泽颜色贴图',
          options: (config.textureSlots.sheenColorMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'sheenRoughness',
          label: '光泽粗糙度',
          min: 0,
          max: 1,
          tooltip: '控制材质表面微观不规则性的程度，影响反射光的散射和表面光泽的均匀性。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.sheenRoughnessMap.value',
          label: '光泽粗糙度贴图',
          options: (config.textureSlots.sheenRoughnessMap as TextureSlotSelect).options,
          allowClear: true,
        },
      ],
    },
    // 法线凹凸置换
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '法线凹凸置换',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.normalMap.value',
          label: '法线贴图',
          options: (config.textureSlots.normalMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'normalScale.x',
          label: '法线扰动',
          min: 0,
          max: 2,
          tooltip: '通过改变法线向量来模拟表面细微的凸起和凹陷，使材质表面看起来更复杂和真实。',
        },
        // 凹凸
        {
          uiType: 'InputSlider',
          fieldName: 'bumpScale',
          label: '凹凸强度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.bumpMap.value',
          label: '凹凸贴图',
          options: (config.textureSlots.bumpMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'displacementScale',
          label: '置换强度',
          min: 0,
          max: 1,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.displacementMap.value',
          label: '置换贴图',
          options: (config.textureSlots.displacementMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'displacementBias',
          label: '置换偏移',
          min: 0,
          max: 1,
        },
      ],
    },
    // 透射
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '透射',
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'transmission',
          label: '透射率',
          min: 0,
          max: 1,
          tooltip: '决定光线在穿透材质时的程度，适用于透明或半透明材质。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.transmissionMap.value',
          label: '透射贴图',
          options: (config.textureSlots.transmissionMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'attenuationDistance',
          label: '衰减距离',
          min: 0,
          max: 1,
          tooltip: '定义光线传播时衰减的距离，常用于模拟光源或材质的衰减效应。',
        },
        {
          uiType: 'ColorPicker',
          fieldName: 'attenuationColor',
          label: '衰减颜色',
          type: ['pure'],
          tooltip: '设定光线衰减过程中的颜色变化，影响物体的光照表现。',
        },
      ],
    },
    // 厚度
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '厚度',
      children: [
        {
          uiType: 'InputSlider',
          fieldName: 'thickness',
          label: '厚度',
          min: 0,
          max: 5,
          tooltip: '材质的物理厚度，影响透明度、折射、色散等光学特性。',
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.thicknessMap.value',
          label: '厚度贴图',
          options: (config.textureSlots.thicknessMap as TextureSlotSelect).options,
          allowClear: true,
        },
      ],
    },
    // AO
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: 'AO',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.aoMap.value',
          label: 'AO贴图',
          options: (config.textureSlots.aoMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'InputSlider',
          fieldName: 'aoMapIntensity',
          label: 'AO强度',
          min: 0,
          max: 1,
        },
      ],
    },
    // 环境与光照
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '环境与光照',
      children: [
        {
          uiType: 'Select',
          fieldName: 'textureSlots.envMap.value',
          label: '环境贴图',
          options: (config.textureSlots.envMap as TextureSlotSelect).options,
          allowClear: true,
        },
        {
          uiType: 'Select',
          fieldName: 'textureSlots.lightMap.value',
          label: '光照贴图',
          options: (config.textureSlots.lightMap as TextureSlotSelect).options,
          allowClear: true,
        },
      ],
    },
    // 渲染设置
    {
      uiType: 'Group',
      showConnectionLine: true,
      showChildren: false,
      label: '渲染',
      children: [
        {
          uiType: 'CheckBox',
          fieldName: 'alphaToCoverage',
          label: '透明多采样',
          tooltip:
            ' 应用多重采样抗锯齿（MSAA）技术以提高透明材质边缘的渲染质量，特别适合透明度渐变的场景。',
        },
        {
          uiType: 'Select',
          fieldName: 'side',
          label: '渲染面',
          options: [
            { key: 0, label: '正面' },
            { key: 1, label: '背面' },
            { key: 2, label: '双面' },
          ],
          tooltip:
            '确定渲染的几何面向（正面、背面或双面），与三角形法线方向和裁剪面剔除机制紧密相关，影响渲染效率和视觉效果。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'depthWrite',
          label: '深度写入',
          tooltip:
            '控制渲染对象是否将其深度值写入深度缓冲区，用于定义遮挡关系和优化渲染性能，通常在处理不透明物体时启用。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'depthTest',
          label: '深度测试',
          tooltip:
            '基于像素深度值与深度缓冲区的比较结果，决定当前像素是否被渲染，用于实现正确的前后遮挡效果。',
        },
        {
          uiType: 'CheckBox',
          fieldName: 'fog',
          label: '雾效',
          tooltip: '决定是否受到场景中雾气的影响。',
        },
      ],
    },
  ];
}
